/**
 * Turning a requested action into the events it produces.
 *
 * This is where the server's authority lives, and it is pure so it can be
 * tested without a socket. It emits unsequenced events; the caller stamps them
 * with id, seq, ts and actor before appending them to the log.
 *
 * Correction proposals are deliberately absent: a proposal is pending
 * server-side state awaiting a second player, not a fact about the game, so it
 * lives in the server until it is confirmed and becomes `correction.applied`.
 */

import type { SeatId, TerritoryId } from '../ids.js';
import { resolveDiceRoll } from '../odds/dice.js';
import {
  classifySet,
  setValue,
  territoryBonusFor,
  type Card,
} from '../rules/cards.js';
import { nextActiveSeat, nextPhase } from '../rules/machine.js';
import { territoriesHeldBy, type MapIndex } from '../rules/adjacency.js';
import { computeReinforcements } from '../rules/reinforcement.js';
import {
  handSize,
  ownerMap,
  type GameState,
} from '../state/types.js';
import type { ActionRequest } from './actions.js';
import type { GameEvent } from './events.js';

export interface DeriveInput {
  readonly state: GameState;
  readonly index: MapIndex;
  readonly request: ActionRequest;
}

export function deriveEvents(input: DeriveInput): readonly GameEvent[] {
  const { state, index, request } = input;
  const { action, actor } = request;
  const rules = state.rules;
  if (rules === null) return [];

  switch (action.type) {
    case 'startGame':
      return [
        {
          type: 'game.started',
          payload: {
            seatOrder: state.seats.filter((seat) => !seat.isNeutral).map((seat) => seat.id),
          },
        },
      ];

    case 'assignTerritories':
      return [{ type: 'setup.assigned', payload: { assignments: action.assignments } }];

    case 'beginPlay': {
      // Starting a game that is already running would reset the turn and lose
      // whatever had happened in it. A real table managed to press this three
      // times. The validator warns, but the table can overrule a warning, and
      // "warn, never block" must not extend to destroying the game -- so this
      // produces nothing rather than something harmful.
      if (state.turn !== null) return [];
      const first = state.seatOrder[0];
      if (first === undefined) return [];
      return [{ type: 'turn.started', payload: { seat: first, number: 1 } }];
    }

    case 'trade': {
      const setType = classifySet(action.cards, rules) ?? 'mixed';
      const owned = new Set(territoriesHeldBy(index, actor, ownerMap(state)));
      const bonus = territoryBonusFor(action.cards, owned, rules);
      return [
        {
          type: 'cards.traded',
          payload: {
            seat: actor,
            cards: action.cards,
            setType,
            armies: setValue(setType, state.setsTraded, rules),
            bonusTerritories: bonus.territories,
            bonusArmiesEach: bonus.armiesEach,
          },
        },
      ];
    }

    case 'place':
      return [{ type: 'armies.placed', payload: { seat: actor, placements: action.placements } }];

    case 'declareAttack': {
      const defender = state.territories[action.to]?.owner;
      if (defender == null) return [];
      const declared: GameEvent = {
        type: 'attack.declared',
        payload: {
          from: action.from,
          to: action.to,
          attacker: actor,
          defender,
          attackerDice: action.attackerDice,
        },
      };
      if (!rules.combat.defenderAlwaysRollsMax) return [declared];

      // Tables that do not want to be asked every time roll the maximum.
      const defendingArmies = state.territories[action.to]?.armies ?? 0;
      const defenderDice = Math.min(rules.combat.maxDefendDice, Math.max(1, defendingArmies));
      return [declared, { type: 'defense.diceChosen', payload: { defenderDice } }];
    }

    case 'chooseDefenseDice':
      return [{ type: 'defense.diceChosen', payload: { defenderDice: action.defenderDice } }];

    case 'submitDice': {
      const battle = state.turn?.pendingBattle;
      if (!battle) return [];
      const losses = resolveDiceRoll(action.attacker, action.defender);
      const defending = state.territories[battle.to]?.armies ?? 0;
      const conquered = defending - losses.defenderLosses <= 0;
      const events: GameEvent[] = [
        { type: 'dice.rolled', payload: { attacker: action.attacker, defender: action.defender } },
        {
          type: 'battle.resolved',
          payload: {
            attackerLosses: losses.attackerLosses,
            defenderLosses: losses.defenderLosses,
            conquered,
          },
        },
      ];
      // Taking ground moves the turn into its occupation step. Without this
      // the board carries a pending occupation while still saying it is in the
      // attack phase, and every screen keyed to the phase -- which is all of
      // them -- offers to roll again instead of to move armies in.
      if (conquered) events.push({ type: 'phase.entered', payload: { phase: 'occupy' } });
      return events;
    }

    case 'occupy': {
      const occupation = state.turn?.pendingOccupation;
      if (!occupation) return [];
      const events: GameEvent[] = [
        {
          type: 'territory.occupied',
          payload: { from: occupation.from, to: occupation.to, armies: action.armies },
        },
      ];

      const victim = state.territories[occupation.to]?.owner ?? null;
      const owners = nextOwners(state, occupation.to, actor);
      if (victim !== null && victim !== actor && !ownsAnything(owners, victim)) {
        events.push(eliminationEvent(state, victim, actor, rules.elimination.captureCards));
      }

      const winner = soleOwner(state, owners);
      if (winner !== null) {
        events.push({ type: 'game.ended', payload: { winner } });
        return events;
      }
      // Armies are in; back to attacking, which is where the player was.
      events.push({ type: 'phase.entered', payload: { phase: 'attack' } });
      return events;
    }

    case 'endAttack':
      return state.turn?.pendingBattle ? [{ type: 'attack.ended', payload: {} }] : [];

    case 'fortify':
      return [
        {
          type: 'fortify.performed',
          payload: { from: action.from, to: action.to, armies: action.armies },
        },
      ];

    case 'adjustAudit':
      return [
        {
          type: 'audit.adjusted',
          payload: {
            territory: action.territory,
            from: state.territories[action.territory]?.armies ?? 0,
            to: action.armies,
          },
        },
      ];

    case 'identifyCard':
      return [
        { type: 'card.identified', payload: { seat: actor, slot: action.slot, card: action.card } },
      ];

    case 'advancePhase': {
      // Leaving the attack phase breaks off any attack still in progress,
      // rather than carrying a half-finished battle into the next phase.
      const breakOff: GameEvent[] = state.turn?.pendingBattle
        ? [{ type: 'attack.ended', payload: {} }]
        : [];
      const phase = nextPhase(state);
      if (phase === null) return [...breakOff, ...endOfTurn(state, actor)];
      // The territory and continent draft lands when placement opens, which
      // is after any trades, so a traded set adds to it rather than racing it.
      if (phase === 'reinforcePlace') {
        return [
          ...breakOff,
          { type: 'phase.entered', payload: { phase } },
          {
            type: 'reinforcements.calculated',
            payload: {
              seat: actor,
              breakdown: computeReinforcements({
                index,
                seat: actor,
                ownerOf: ownerMap(state),
                rules,
              }),
            },
          },
        ];
      }
      // Entering the card phase is what actually awards the card, and it
      // starts unidentified -- the player names it whenever it matters.
      if (phase === 'cardDraw') {
        return [
          ...breakOff,
          { type: 'phase.entered', payload: { phase } },
          { type: 'card.drawn', payload: { seat: actor, card: null } },
        ];
      }
      return [...breakOff, { type: 'phase.entered', payload: { phase } }];
    }

    case 'endTurn':
      return endOfTurn(state, actor);

    default:
      return [];
  }
}

function endOfTurn(state: GameState, actor: SeatId): readonly GameEvent[] {
  const events: GameEvent[] = [{ type: 'turn.ended', payload: { seat: actor } }];
  const next = nextActiveSeat(state, actor);
  if (next === null) return events;
  events.push({
    type: 'turn.started',
    payload: { seat: next, number: (state.turn?.number ?? 0) + 1 },
  });
  return events;
}

function eliminationEvent(
  state: GameState,
  victim: SeatId,
  by: SeatId,
  captureCards: boolean,
): GameEvent {
  const hand = state.hands[victim] ?? { visible: false, count: 0 };
  const held = handSize(hand);
  if (!captureCards) {
    return { type: 'player.eliminated', payload: { seat: victim, by, cardsTransferred: 0, cards: null } };
  }

  // The cards are physical: they change hands whether or not anyone told the
  // app what they are. Only name them when every slot is already identified.
  const known: Card[] = hand.visible
    ? hand.slots.flatMap((slot) => (slot.kind === 'known' ? [slot.card] : []))
    : [];
  return {
    type: 'player.eliminated',
    payload: {
      seat: victim,
      by,
      cardsTransferred: held,
      cards: known.length === held ? known : null,
    },
  };
}

/** Ownership as it will be once this occupation is applied. */
function nextOwners(
  state: GameState,
  conquered: TerritoryId,
  attacker: SeatId,
): ReadonlyMap<TerritoryId, SeatId | null> {
  const owners = new Map(ownerMap(state));
  owners.set(conquered, attacker);
  return owners;
}

function ownsAnything(
  owners: ReadonlyMap<TerritoryId, SeatId | null>,
  seat: SeatId,
): boolean {
  for (const owner of owners.values()) if (owner === seat) return true;
  return false;
}

function soleOwner(
  state: GameState,
  owners: ReadonlyMap<TerritoryId, SeatId | null>,
): SeatId | null {
  if (state.rules?.victory.condition !== 'conquest') return null;
  if (state.status !== 'playing') return null;
  const distinct = new Set<SeatId>();
  for (const owner of owners.values()) {
    if (owner === null) return null;
    distinct.add(owner);
  }
  if (distinct.size !== 1) return null;
  const [only] = [...distinct];
  const seat = state.seats.find((s) => s.id === only);
  return seat && !seat.isNeutral ? only! : null;
}
